import React from "react";
import { motion } from "framer-motion";
import { Badge } from "components/common/Badge";
import { CardProps, CardColor } from "./Card.types";

export const Card: React.FC<CardProps> = ({
  icon,
  title,
  description,
  color = "blue",
  badge,
  badgeVariant = "info",
  action,
  className = "",
  children,
  onClick,
  disabled = false,
  animate = true,
  delay = 0,
}) => {
  // Color utilities
  const getColorClasses = (color: CardColor) => {
    const colorMap: Record<CardColor, string> = {
      blue: "bg-hue-blue/10 text-hue-blue border-hue-blue/30",
      green: "bg-hue-emerald/10 text-hue-emerald border-hue-emerald/30",
      purple: "bg-hue-violet/10 text-hue-violet border-hue-violet/30",
      indigo: "bg-hue-indigo/10 text-hue-indigo border-hue-indigo/30",
      emerald: "bg-hue-emerald/10 text-hue-emerald border-hue-emerald/30",
      cyan: "bg-hue-cyan/10 text-hue-cyan border-hue-cyan/30",
      teal: "bg-hue-teal/10 text-hue-teal border-hue-teal/30",
      violet: "bg-hue-violet/10 text-hue-violet border-hue-violet/30",
      brand: "bg-primary/10 text-primary border-primary/30",
      orange: "bg-secondary/10 text-secondary border-secondary/30",
      red: "bg-danger/10 text-danger border-danger/30",
    };
    return colorMap[color];
  };

  // Base card classes
  const baseClasses = `
    bg-gradient-to-br from-card to-muted rounded-xl border border-border
    transition-all duration-300 p-6
    ${className}
  `;

  // Interactive classes
  const interactiveClasses =
    onClick && !disabled
      ? "group cursor-pointer hover:scale-[1.02] hover:border-border hover:bg-gradient-to-br hover:from-muted hover:to-muted/80 hover:shadow-xl"
      : "cursor-default";

  const disabledClasses = disabled ? "opacity-50 cursor-not-allowed" : "";

  // Render header if we have header elements
  const renderHeader = () => {
    if (!icon && !title && !description && !badge && !action) return null;

    return (
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          {icon && (
            <div
              className={`w-10 h-10 ${getColorClasses(
                color
              )} rounded-lg flex items-center justify-center`}
            >
              <div className="text-lg">{icon}</div>
            </div>
          )}
          {(title || description) && (
            <div>
              {title && <h3 className="text-xl font-semibold text-foreground">{title}</h3>}
              {description && <p className="text-muted-foreground text-sm">{description}</p>}
            </div>
          )}
        </div>
        <div className="flex items-center gap-3">
          {badge && <Badge variant={badgeVariant}>{badge}</Badge>}
          {action && <div className="flex-shrink-0">{action}</div>}
        </div>
      </div>
    );
  };

  const cardContent = (
    <div
      onClick={onClick}
      className={`${baseClasses} ${interactiveClasses} ${disabledClasses} flex flex-col gap-6 h-full`}
    >
      {renderHeader()}
      {children}
    </div>
  );

  if (!animate) {
    return <div className="flex flex-col h-full">{cardContent}</div>;
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay, duration: 0.3, ease: "easeOut" }}
      className="flex flex-col h-full"
    >
      {cardContent}
    </motion.div>
  );
};
